/**
 * Pure helpers for the dashboard calendar (month grid, Sunday-first weeks).
 * Days are plain `YYYY-MM-DD` strings so timezones cannot shift an event by a day.
 */

const DAY_MS = 86_400_000;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

/** `YYYY-MM-DD` → days since epoch (UTC, so DST never bites). */
export function dayIndex(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function dayFromIndex(index: number): string {
  return new Date(index * DAY_MS).toISOString().slice(0, 10);
}

export function addDays(day: string, n: number): string {
  return dayFromIndex(dayIndex(day) + n);
}

/** First day of a `YYYY-MM` month. */
export function monthStart(month: string): string {
  return `${month}-01`;
}

export function monthEnd(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(last).padStart(2, "0")}`;
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(day: string): number {
  return new Date(dayIndex(day) * DAY_MS).getUTCDay();
}

/** Visible grid for a month: from the Sunday on/before the 1st to the Saturday on/after the last day. */
export function monthGridRange(month: string): { start: string; end: string } {
  const first = monthStart(month);
  const last = monthEnd(month);
  return {
    start: addDays(first, -weekday(first)),
    end: addDays(last, 6 - weekday(last)),
  };
}

/** Weeks (arrays of 7 `YYYY-MM-DD`) covering the grid range. */
export function gridWeeks(month: string): string[][] {
  const { start, end } = monthGridRange(month);
  const weeks: string[][] = [];
  for (let i = dayIndex(start); i <= dayIndex(end); i += 7) {
    weeks.push(Array.from({ length: 7 }, (_, k) => dayFromIndex(i + k)));
  }
  return weeks;
}

export type DayRange = { start: string; end: string };

/**
 * An event's day span. Prefers the `eventStart`/`eventEnd` strings (already
 * `YYYY-MM-DD`); falls back to the ISO date fields. A missing end = one day.
 */
export function eventDayRange(ev: Record<string, unknown>): DayRange | null {
  const pick = (a: unknown, b: unknown): string | null => {
    for (const v of [a, b]) {
      if (typeof v === "string" && ISO_DAY.test(v)) return v.slice(0, 10);
      if (v instanceof Date && !Number.isNaN(v.getTime())) {
        return v.toISOString().slice(0, 10);
      }
    }
    return null;
  };
  const start = pick(ev.eventStart, ev.eventStartDate);
  if (!start) return null;
  const end = pick(ev.eventEnd, ev.eventEndDate) ?? start;
  return dayIndex(end) < dayIndex(start)
    ? { start, end: start }
    : { start, end };
}

export type WeekSegment<T> = {
  event: T;
  /** 0-based column within the week (Sunday = 0). */
  startCol: number;
  endCol: number;
  /** 0-based stacking lane. */
  lane: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
};

/**
 * Lays out the events that touch a week as horizontal segments with stacking
 * lanes (greedy: earliest start first, longer spans first on ties). Lanes are
 * unbounded here; the renderer decides how many to show.
 */
export function layoutWeek<T>(
  week: string[],
  events: T[],
  rangeOf: (ev: T) => DayRange | null,
): WeekSegment<T>[] {
  const weekStart = dayIndex(week[0]);
  const weekEnd = dayIndex(week[week.length - 1]);

  const spans = events
    .map((event) => {
      const r = rangeOf(event);
      if (!r) return null;
      const s = dayIndex(r.start);
      const e = dayIndex(r.end);
      if (e < weekStart || s > weekEnd) return null;
      return {
        event,
        startCol: Math.max(s, weekStart) - weekStart,
        endCol: Math.min(e, weekEnd) - weekStart,
        continuesBefore: s < weekStart,
        continuesAfter: e > weekEnd,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort(
      (a, b) =>
        a.startCol - b.startCol ||
        b.endCol - b.startCol - (a.endCol - a.startCol),
    );

  const laneEnds: number[] = []; // last occupied column per lane
  return spans.map((span) => {
    let lane = laneEnds.findIndex((end) => end < span.startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(span.endCol);
    } else {
      laneEnds[lane] = span.endCol;
    }
    return { ...span, lane };
  });
}

/** How many segments beyond `maxLanes` cover each column of the week. */
export function hiddenPerDay<T>(
  segments: WeekSegment<T>[],
  maxLanes: number,
): number[] {
  const hidden = new Array(7).fill(0);
  for (const seg of segments) {
    if (seg.lane < maxLanes) continue;
    for (let c = seg.startCol; c <= seg.endCol; c += 1) hidden[c] += 1;
  }
  return hidden;
}
